import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowRight, Shield, Wallet, Users } from "lucide-react";

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-[calc(100vh-4rem)]" data-testid="landing-page">
      {/* Hero */}
      <section className="relative overflow-hidden grain-overlay">
        <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, #f8fafc 0%, #f0fdf4 40%, #ecfdf5 70%, #f0fdfa 100%)" }} />
        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-72 h-72 rounded-full opacity-30" style={{ background: "radial-gradient(circle, #a7f3d0, transparent 70%)" }} />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full opacity-20" style={{ background: "radial-gradient(circle, #99f6e4, transparent 70%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32 lg:py-40">
          <div className="max-w-3xl">
            <p
              className="text-sm font-medium tracking-widest uppercase text-emerald-600 mb-5 animate-fade-in-up stagger-1"
              data-testid="hero-tagline"
            >
              Earn extra money for your content
          
            </p>
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-tight mb-6 animate-fade-in-up stagger-2"
              data-testid="hero-title"
            >
              <span className="text-foreground">Show your support,</span>
              <br />
              <span className="gradient-text">for free!</span>
            </h1>
            <p
              className="text-base md:text-lg leading-relaxed text-foreground/60 mb-10 max-w-xl animate-fade-in-up stagger-3"
              data-testid="hero-description"
            >
              Click Colony is a way for content creators to monetize their views and get their viewers involved. By simply clicking a button users can help you earn extra revenue and it costs them nothing but a few seconds.
            </p>
            <div className="flex gap-3 animate-fade-in-up stagger-4">
              {user ? (
                <Button
                  size="lg"
                  className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white transition-all duration-200 hover:shadow-lg hover:shadow-emerald-600/20"
                  onClick={() => navigate("/dashboard")}
                  data-testid="hero-dashboard-btn"
                >
                  Go to Dashboard <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <>
                  <Button
                    size="lg"
                    className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white transition-all duration-200 hover:shadow-lg hover:shadow-emerald-600/20"
                    onClick={() => navigate("/register")}
                    data-testid="hero-register-btn"
                  >
                    Get Started <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-sm transition-all duration-200 hover:bg-foreground hover:text-background"
                    onClick={() => navigate("/login")}
                    data-testid="hero-login-btn"
                  >
                    Log in
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-20 md:py-28">
        <div className="grid md:grid-cols-3 gap-6">
          <div
            className="p-6 md:p-8 border border-border rounded-sm card-hover animate-fade-in-up stagger-1"
            data-testid="feature-balance"
          >
            <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center mb-5">
              <Wallet className="h-5 w-5 text-emerald-600" />
            </div>
            <h3 className="font-heading text-lg font-semibold tracking-tight mb-2">Detailed Tracking</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              View all of your stats from your dashboard.
            </p>
          </div>
          <div
            className="p-6 md:p-8 border border-border rounded-sm card-hover animate-fade-in-up stagger-2"
            data-testid="feature-withdraw"
          >
            <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center mb-5">
              <Shield className="h-5 w-5 text-emerald-600" />
            </div>
            <h3 className="font-heading text-lg font-semibold tracking-tight mb-2">Secure Withdrawals</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Request withdrawals securely. Every transaction is logged and traceable.
            </p>
          </div>
          <div
            className="p-6 md:p-8 border border-border rounded-sm card-hover animate-fade-in-up stagger-3"
            data-testid="feature-admin"
          >
            <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center mb-5">
              <Users className="h-5 w-5 text-emerald-600" />
            </div>
            <h3 className="font-heading text-lg font-semibold tracking-tight mb-2">Customize Your Page</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Customize what people see when they click to support you!
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} Click Colony. All rights reserved.
          </p>
          <div className="flex gap-6">
            <button
              onClick={() => navigate("/faq")}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="footer-faq-link"
            >
              FAQ
            </button>
            <button
              onClick={() => navigate("/contact")}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="footer-contact-link"
            >
              Contact
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
